import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CSSProperties } from "react";
import { FaInstagram as FaInstagramRaw, FaWhatsapp as FaWhatsappRaw } from "react-icons/fa";
import YogyismLogoSvg from "./yogyismLogoSvg";
import LoginModal from "./LoginModal";
import ContactModal from "./Contactmodal";
import SocialQrModal from "./SocialQrModal";
import { onAuthChange, logout } from "../../services/auth.service";
import { getSharedPlaying } from "./playingBus";
import { pauseSwiper, resumeSwiper } from "./swiperControl";
import { useNavigate } from "react-router-dom";
import { FONT_DISPLAY, FONT_MONO } from "theme/fonts";
import { useUi } from "i18n/ui";
import LanguageSwitch from "i18n/LanguageSwitch";

// ── Consent helpers (inline — no separate file needed) ────────────────────────
const CONSENT_KEY     = "yogyism:consentAccepted:v1";
const CONSENT_VERSION = "v1";

function hasConsented(): boolean {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return parsed?.version === CONSENT_VERSION;
  } catch { return false; }
}

function writeConsent() {
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({
      version: CONSENT_VERSION,
      acceptedAt: Date.now(),
    }));
  } catch {}
}

// ── Consent Modal ─────────────────────────────────────────────────────────────
function ConsentModal({
  onAccept,
  onDecline,
}: {
  onAccept: () => void;
  onDecline: () => void;
}) {
  const [checked, setChecked]         = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const t = useUi().consent;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 3000,
      background: "rgba(3,7,18,0.93)", backdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16, fontFamily: FONT_MONO,
    }}>
      <div style={{
        background: "#0b1628", border: "1px solid #1e3a5f", borderRadius: 18,
        width: "min(460px, 96vw)", maxHeight: "90vh", overflowY: "auto",
        padding: "22px 22px 18px", display: "flex", flexDirection: "column",
        boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
        scrollbarWidth: "thin", scrollbarColor: "#1e3a5f transparent",
      }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <span style={{ fontSize: 26 }}>✨</span>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: "#e2e8f0", margin: 0 }}>
            {t.title}
          </h2>
        </div>

        {/* Body text */}
        {t.intro.map((text, i) => (
          <p key={i} style={{
            fontSize: 13, color: "#a8bdd1", lineHeight: 1.55,
            margin: i === 0 ? "0 0 8px" : "8px 0",
          }}>{text}</p>
        ))}

        {/* Read more toggle */}
        <button
          onClick={() => setShowDetails(v => !v)}
          style={{
            alignSelf: "flex-start", background: "none", border: "none",
            color: "#38bdf8", fontSize: 12, fontWeight: 700, cursor: "pointer",
            padding: "4px 0", marginTop: 2,
          }}
          type="button"
        >
          {showDetails ? t.hideDetails : t.showDetails}
        </button>

        {showDetails && (
          <div style={{
            background: "#07101e", border: "1px solid #1e293b",
            borderRadius: 12, padding: "14px 16px", marginTop: 8,
          }}>
            {t.details.map(({ title, body }) => (
              <div key={title} style={{ marginBottom: 14 }}>
                <div style={{
                  fontSize: 10.5, fontWeight: 800, color: "#38bdf8",
                  letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 5,
                }}>{title}</div>
                <div style={{ fontSize: 12, color: "#a8bdd1", lineHeight: 1.55 }}>{body}</div>
              </div>
            ))}
          </div>
        )}

        {/* Checkbox */}
        <label style={{
          display: "flex", alignItems: "flex-start", gap: 9,
          marginTop: 16, cursor: "pointer",
        }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={e => setChecked(e.target.checked)}
            style={{ marginTop: 2, width: 16, height: 16, accentColor: "#38bdf8", cursor: "pointer", flexShrink: 0 }}
          />
          <span style={{ fontSize: 12.5, color: "#cfe3f5", lineHeight: 1.4 }}>
            {t.checkbox}
          </span>
        </label>

        {/* Buttons */}
        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button
            onClick={onDecline}
            style={{
              background: "transparent", color: "#94a3b8",
              border: "1px solid #334155", borderRadius: 10,
              padding: "11px 16px", fontSize: 13, fontWeight: 700,
              cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            {t.decline}
          </button>
          <button
            onClick={onAccept}
            disabled={!checked}
            style={{
              flex: 1, background: "#38bdf8", color: "#031018",
              border: "none", borderRadius: 10, padding: "11px 18px",
              fontSize: 13, fontWeight: 800,
              opacity: checked ? 1 : 0.4,
              cursor: checked ? "pointer" : "not-allowed",
            }}
          >
            {t.accept}
          </button>
        </div>
      </div>
    </div>
  );
}

const mono = FONT_MONO;

// ─── All country codes ────────────────────────────────────────────────────────
const COUNTRY_CODES: { code: string; dial: string }[] = [
  { code: "AF", dial: "+93"   }, { code: "AX", dial: "+358"  }, { code: "AL", dial: "+355"  },
  { code: "DZ", dial: "+213"  }, { code: "AS", dial: "+1684" }, { code: "AD", dial: "+376"  },
  { code: "AO", dial: "+244"  }, { code: "AI", dial: "+1"    }, { code: "AQ", dial: "+672"  },
  { code: "AG", dial: "+1"    }, { code: "AR", dial: "+54"   }, { code: "AM", dial: "+374"  },
  { code: "AW", dial: "+297"  }, { code: "AU", dial: "+61"   }, { code: "AT", dial: "+43"   },
  { code: "AZ", dial: "+994"  }, { code: "BS", dial: "+1"    }, { code: "BH", dial: "+973"  },
  { code: "BD", dial: "+880"  }, { code: "BB", dial: "+1246" }, { code: "BY", dial: "+375"  },
  { code: "BE", dial: "+32"   }, { code: "BZ", dial: "+501"  }, { code: "BJ", dial: "+229"  },
  { code: "BM", dial: "+1"    }, { code: "BT", dial: "+975"  }, { code: "BO", dial: "+591"  },
  { code: "BQ", dial: "+599"  }, { code: "BA", dial: "+387"  }, { code: "BW", dial: "+267"  },
  { code: "BV", dial: "+47"   }, { code: "BR", dial: "+55"   }, { code: "IO", dial: "+246"  },
  { code: "BN", dial: "+673"  }, { code: "BG", dial: "+359"  }, { code: "BF", dial: "+226"  },
  { code: "BI", dial: "+257"  }, { code: "CV", dial: "+238"  }, { code: "KH", dial: "+855"  },
  { code: "CM", dial: "+237"  }, { code: "CA", dial: "+1"    }, { code: "KY", dial: "+1"    },
  { code: "CF", dial: "+236"  }, { code: "TD", dial: "+235"  }, { code: "CL", dial: "+56"   },
  { code: "CN", dial: "+86"   }, { code: "CX", dial: "+61"   }, { code: "CC", dial: "+61"   },
  { code: "CO", dial: "+57"   }, { code: "KM", dial: "+269"  }, { code: "CG", dial: "+242"  },
  { code: "CD", dial: "+243"  }, { code: "CK", dial: "+682"  }, { code: "CR", dial: "+506"  },
  { code: "CI", dial: "+225"  }, { code: "HR", dial: "+385"  }, { code: "CU", dial: "+53"   },
  { code: "CW", dial: "+599"  }, { code: "CY", dial: "+357"  }, { code: "CZ", dial: "+420"  },
  { code: "DK", dial: "+45"   }, { code: "DJ", dial: "+253"  }, { code: "DM", dial: "+1"    },
  { code: "DO", dial: "+1"    }, { code: "EC", dial: "+593"  }, { code: "EG", dial: "+20"   },
  { code: "SV", dial: "+503"  }, { code: "GQ", dial: "+240"  }, { code: "ER", dial: "+291"  },
  { code: "EE", dial: "+372"  }, { code: "ET", dial: "+251"  }, { code: "FK", dial: "+500"  },
  { code: "FO", dial: "+298"  }, { code: "FJ", dial: "+679"  }, { code: "FI", dial: "+358"  },
  { code: "FR", dial: "+33"   }, { code: "GF", dial: "+594"  }, { code: "PF", dial: "+689"  },
  { code: "TF", dial: "+262"  }, { code: "GA", dial: "+241"  }, { code: "GM", dial: "+220"  },
  { code: "GE", dial: "+995"  }, { code: "DE", dial: "+49"   }, { code: "GH", dial: "+233"  },
  { code: "GI", dial: "+350"  }, { code: "GR", dial: "+30"   }, { code: "GL", dial: "+299"  },
  { code: "GD", dial: "+1"    }, { code: "GP", dial: "+590"  }, { code: "GU", dial: "+1"    },
  { code: "GT", dial: "+502"  }, { code: "GG", dial: "+44"   }, { code: "GN", dial: "+224"  },
  { code: "GW", dial: "+245"  }, { code: "GY", dial: "+592"  }, { code: "HT", dial: "+509"  },
  { code: "HM", dial: "+672"  }, { code: "VA", dial: "+39"   }, { code: "HN", dial: "+504"  },
  { code: "HK", dial: "+852"  }, { code: "HU", dial: "+36"   }, { code: "IS", dial: "+354"  },
  { code: "IN", dial: "+91"   }, { code: "ID", dial: "+62"   }, { code: "IR", dial: "+98"   },
  { code: "IQ", dial: "+964"  }, { code: "IE", dial: "+353"  }, { code: "IM", dial: "+44"   },
  { code: "IL", dial: "+972"  }, { code: "IT", dial: "+39"   }, { code: "JM", dial: "+1876" },
  { code: "JP", dial: "+81"   }, { code: "JE", dial: "+44"   }, { code: "JO", dial: "+962"  },
  { code: "KZ", dial: "+7"    }, { code: "KE", dial: "+254"  }, { code: "KI", dial: "+686"  },
  { code: "KP", dial: "+850"  }, { code: "KR", dial: "+82"   }, { code: "XK", dial: "+383"  },
  { code: "KW", dial: "+965"  }, { code: "KG", dial: "+996"  }, { code: "LA", dial: "+856"  },
  { code: "LV", dial: "+371"  }, { code: "LB", dial: "+961"  }, { code: "LS", dial: "+266"  },
  { code: "LR", dial: "+231"  }, { code: "LY", dial: "+218"  }, { code: "LI", dial: "+423"  },
  { code: "LT", dial: "+370"  }, { code: "LU", dial: "+352"  }, { code: "MO", dial: "+853"  },
  { code: "MK", dial: "+389"  }, { code: "MG", dial: "+261"  }, { code: "MW", dial: "+265"  },
  { code: "MY", dial: "+60"   }, { code: "MV", dial: "+960"  }, { code: "ML", dial: "+223"  },
  { code: "MT", dial: "+356"  }, { code: "MH", dial: "+692"  }, { code: "MQ", dial: "+596"  },
  { code: "MR", dial: "+222"  }, { code: "MU", dial: "+230"  }, { code: "YT", dial: "+262"  },
  { code: "MX", dial: "+52"   }, { code: "FM", dial: "+691"  }, { code: "MD", dial: "+373"  },
  { code: "MC", dial: "+377"  }, { code: "MN", dial: "+976"  }, { code: "ME", dial: "+382"  },
  { code: "MS", dial: "+1"    }, { code: "MA", dial: "+212"  }, { code: "MZ", dial: "+258"  },
  { code: "MM", dial: "+95"   }, { code: "NA", dial: "+264"  }, { code: "NR", dial: "+674"  },
  { code: "NP", dial: "+977"  }, { code: "NL", dial: "+31"   }, { code: "NC", dial: "+687"  },
  { code: "NZ", dial: "+64"   }, { code: "NI", dial: "+505"  }, { code: "NE", dial: "+227"  },
  { code: "NG", dial: "+234"  }, { code: "NU", dial: "+683"  }, { code: "NF", dial: "+672"  },
  { code: "MP", dial: "+1670" }, { code: "NO", dial: "+47"   }, { code: "OM", dial: "+968"  },
  { code: "PK", dial: "+92"   }, { code: "PW", dial: "+680"  }, { code: "PS", dial: "+970"  },
  { code: "PA", dial: "+507"  }, { code: "PG", dial: "+675"  }, { code: "PY", dial: "+595"  },
  { code: "PE", dial: "+51"   }, { code: "PH", dial: "+63"   }, { code: "PN", dial: "+64"   },
  { code: "PL", dial: "+48"   }, { code: "PT", dial: "+351"  }, { code: "PR", dial: "+1"    },
  { code: "QA", dial: "+974"  }, { code: "RE", dial: "+262"  }, { code: "RO", dial: "+40"   },
  { code: "RU", dial: "+7"    }, { code: "RW", dial: "+250"  }, { code: "BL", dial: "+590"  },
  { code: "SH", dial: "+290"  }, { code: "KN", dial: "+1"    }, { code: "LC", dial: "+1"    },
  { code: "MF", dial: "+590"  }, { code: "PM", dial: "+508"  }, { code: "VC", dial: "+1"    },
  { code: "WS", dial: "+685"  }, { code: "SM", dial: "+378"  }, { code: "ST", dial: "+239"  },
  { code: "SA", dial: "+966"  }, { code: "SN", dial: "+221"  }, { code: "RS", dial: "+381"  },
  { code: "SC", dial: "+248"  }, { code: "SL", dial: "+232"  }, { code: "SG", dial: "+65"   },
  { code: "SX", dial: "+1721" }, { code: "SK", dial: "+421"  }, { code: "SI", dial: "+386"  },
  { code: "SB", dial: "+677"  }, { code: "SO", dial: "+252"  }, { code: "ZA", dial: "+27"   },
  { code: "GS", dial: "+500"  }, { code: "SS", dial: "+211"  }, { code: "ES", dial: "+34"   },
  { code: "LK", dial: "+94"   }, { code: "SD", dial: "+249"  }, { code: "SR", dial: "+597"  },
  { code: "SJ", dial: "+47"   }, { code: "SZ", dial: "+268"  }, { code: "SE", dial: "+46"   },
  { code: "CH", dial: "+41"   }, { code: "TW", dial: "+886"  }, { code: "TJ", dial: "+992"  },
  { code: "TZ", dial: "+255"  }, { code: "TH", dial: "+66"   }, { code: "TL", dial: "+670"  },
  { code: "TG", dial: "+228"  }, { code: "TK", dial: "+690"  }, { code: "TO", dial: "+676"  },
  { code: "TT", dial: "+1868" }, { code: "TN", dial: "+216"  }, { code: "TR", dial: "+90"   },
  { code: "TM", dial: "+993"  }, { code: "TC", dial: "+1"    }, { code: "TV", dial: "+688"  },
  { code: "UG", dial: "+256"  }, { code: "UA", dial: "+380"  }, { code: "AE", dial: "+971"  },
  { code: "GB", dial: "+44"   }, { code: "UM", dial: "+1"    }, { code: "US", dial: "+1"    },
  { code: "UY", dial: "+598"  }, { code: "UZ", dial: "+998"  }, { code: "VU", dial: "+678"  },
  { code: "VE", dial: "+58"   }, { code: "VN", dial: "+84"   }, { code: "VG", dial: "+1"    },
  { code: "VI", dial: "+1"    }, { code: "WF", dial: "+681"  }, { code: "EH", dial: "+212"  },
  { code: "YE", dial: "+967"  }, { code: "ZM", dial: "+260"  }, { code: "ZW", dial: "+263"  },
];

// ─── ExternalLinkIcon ─────────────────────────────────────────────────────────
function ExternalLinkIcon({ size = 10, color = "currentColor" }: { size?: number; color?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg" width={size} height={size}
      viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

interface SlideToolbarProps {
  accent?: string;
  accentRgb?: string;
}

const SlideToolbar: React.FC<SlideToolbarProps> = ({
  accent = "#378add",
  accentRgb = "55,138,221",
}) => {  const ui = useUi();

  const fontLinkRef = useRef<HTMLLinkElement | null>(null);
  const [logoHovered, setLogoHovered]     = useState(false);
  const [showLogin, setShowLogin]         = useState(false);
  const [showContact, setShowContact]     = useState(false);
  const [showWhatsApp, setShowWhatsApp]   = useState(false);
  const [showInstagram, setShowInstagram] = useState(false);
  const [showConsent, setShowConsent]     = useState(false); // ✅ NEW
  const [user, setUser]                   = useState<any>(null);
  const [isMobile, setIsMobile]           = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 640
  );
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthChange((u) => setUser(u));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    const existing = document.querySelector('link[href*="Space+Grotesk"]') as HTMLLinkElement | null;
    if (!existing) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
      document.head.appendChild(link);
      fontLinkRef.current = link;
    }
    return () => {
      if (fontLinkRef.current && document.head.contains(fontLinkRef.current)) {
        document.head.removeChild(fontLinkRef.current);
        fontLinkRef.current = null;
      }
    };
  }, []);

  // ── Pause/resume swiper when LoginModal opens/closes ─────────────────────
  const prevOpenRef   = useRef<boolean>(false);
  const wasPlayingRef = useRef<boolean>(false);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = showLogin;
    if (showLogin && !wasOpen) {
      wasPlayingRef.current = getSharedPlaying();
      window.dispatchEvent(new Event("yogyism:play"));
    } else if (!showLogin && wasOpen) {
      if (wasPlayingRef.current) window.dispatchEvent(new Event("yogyism:resume"));
    }
  }, [showLogin]);

  const prevContactRef       = useRef<boolean>(false);
  const wasPlayingContactRef = useRef<boolean>(false);
  useEffect(() => {
    const wasOpen = prevContactRef.current;
    prevContactRef.current = showContact;
    if (showContact && !wasOpen) {
      wasPlayingContactRef.current = getSharedPlaying();
      window.dispatchEvent(new Event("yogyism:stopAudio"));
      pauseSwiper();
    } else if (!showContact && wasOpen) {
      if (wasPlayingContactRef.current) resumeSwiper();
    }
  }, [showContact]);

  const prevWhatsAppRef       = useRef<boolean>(false);
  const wasPlayingWhatsAppRef = useRef<boolean>(false);
  useEffect(() => {
    const wasOpen = prevWhatsAppRef.current;
    prevWhatsAppRef.current = showWhatsApp;
    if (showWhatsApp && !wasOpen) {
      wasPlayingWhatsAppRef.current = getSharedPlaying();
      window.dispatchEvent(new Event("yogyism:stopAudio"));
      pauseSwiper();
    } else if (!showWhatsApp && wasOpen) {
      if (wasPlayingWhatsAppRef.current) resumeSwiper();
    }
  }, [showWhatsApp]);

  const prevInstagramRef       = useRef<boolean>(false);
  const wasPlayingInstagramRef = useRef<boolean>(false);
  useEffect(() => {
    const wasOpen = prevInstagramRef.current;
    prevInstagramRef.current = showInstagram;
    if (showInstagram && !wasOpen) {
      wasPlayingInstagramRef.current = getSharedPlaying();
      window.dispatchEvent(new Event("yogyism:stopAudio"));
      pauseSwiper();
    } else if (!showInstagram && wasOpen) {
      if (wasPlayingInstagramRef.current) resumeSwiper();
    }
  }, [showInstagram]);

  // ✅ KEY HANDLER — intercepts the Login button click
  const handleLoginClick = () => {
    setShowLogin(true);
  };

  // ✅ User accepted consent → save it, close consent popup, open login
  const handleConsentAccept = () => {
    writeConsent();
    setShowConsent(false);
    setShowLogin(true);
  };

  // ✅ User declined → just close consent popup, resume slideshow
  const handleConsentDecline = () => {
    setShowConsent(false);
    window.dispatchEvent(new Event("yogyism:resume"));
  };

  const logoColor = logoHovered ? "#5fb3ff" : "white";

  const headerStars: [number, number, string, number][] = [
    [30, 18, accent, .18], [85, 8, "white", .28], [160, 30, "white", .18], [240, 12, "#b0a8f0", .2],
    [320, 25, "white", .22], [400, 7, "white", .28], [560, 15, "white", .24], [640, 28, "white", .2],
    [720, 10, "#b0a8f0", .18], [800, 22, "white", .2], [860, 40, "white", .22],
  ];

  // ── Header is a 3-column CSS Grid (logo | title | actions) instead of the
  //    old flex+absolute-centered-title layout. With absolute centering, the
  //    title sat at exactly 50% of the *whole header*, regardless of how wide
  //    the logo/buttons were — on desktop especially, that meant it wasn't
  //    truly centered between the logo and the button group, and on medium
  //    widths it could sit right on top of Support Us / Contact Us / Login.
  //    The title is now a genuine grid child in the middle column, so it
  //    centers itself in whatever space is actually left between the logo
  //    (col 1) and the actions (col 3) — always correct, on every width.
  const header: CSSProperties = {
    display: "grid",
    // 1fr | auto | 1fr — the two outer columns are always EQUAL width
    // (whatever space is left after the middle column), so the title in
    // the middle column sits at the true visual center of the header no
    // matter how wide the logo (col 1) or the button group (col 3) are.
    // (auto/1fr/auto would only center the title correctly if col1 and
    // col3 happened to be the same width, which they usually aren't —
    // that was the remaining centering bug.)
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    columnGap: 8,
    padding: "5px clamp(10px,2vw,24px)",
    background: "#071520", borderBottom: `1px solid rgba(${accentRgb},0.22)`,
    position: "relative", flexShrink: 0,
  };
  const btnBase: CSSProperties = {
    background: "transparent", border: `1px solid rgba(${accentRgb},0.55)`,
    padding: "5px 14px", borderRadius: 6, color: "white", cursor: "pointer",
    fontSize: "clamp(11px,0.9vw,14px)", fontFamily: mono,
    letterSpacing: 1, textTransform: "uppercase",
  };
  const btnDanger: CSSProperties = {
    ...btnBase, border: "1px solid rgba(220,90,90,0.6)", color: "#ff9a9a",
  };
  const btnContact: CSSProperties = {
    ...btnBase, border: `1px solid rgba(${accentRgb},0.35)`,
    transition: "border-color 0.2s, color 0.2s", opacity: 0.85,
  };
  const logoIcon: CSSProperties = {
    width: "clamp(24px,3.5vw,50px)", height: "clamp(24px,3.5vw,50px)",
    background: "#0c2a44", border: "1px solid rgba(55,138,221,0.35)",
    borderRadius: "50%", display: "flex", alignItems: "center",
    justifyContent: "center", fontSize: "clamp(11px,1.2vw,15px)", overflow: "hidden",
  };
  const logoName: CSSProperties = {
    fontSize: "clamp(15px,1.5vw,22px)", fontWeight: 600, fontStyle: "normal",
    letterSpacing: "0.18em", color: logoColor, textTransform: "uppercase",
    fontFamily: FONT_DISPLAY, lineHeight: 1, transition: "color 0.2s, text-shadow 0.2s",
    textShadow: logoHovered ? `0 0 14px rgba(${accentRgb},0.55)` : `0 0 8px rgba(${accentRgb},0.20)`,
    paddingBottom: 2,
    backgroundImage: `linear-gradient(${logoColor}, ${logoColor})`,
    backgroundRepeat: "no-repeat", backgroundPosition: "0 100%",
    backgroundSize: logoHovered ? "100% 2px" : "0% 2px",
    transitionProperty: "color, text-shadow, background-size",
    transitionDuration: "0.25s",
  };

  return (
    <>
      <div style={header}>
        {/* Star background */}
        <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", overflow: "hidden", pointerEvents: "none" }}>
          <svg style={{ width: "100%", height: "100%" }} viewBox="0 0 900 90" preserveAspectRatio="xMidYMid slice">
            {headerStars.map(([cx, cy, f, o], i) => (
              <circle key={i} cx={cx} cy={cy} r="1" fill={f} opacity={o} />
            ))}
          </svg>
        </div>

        {/* Logo (+ Support, desktop only) — grid column 1, pinned to the start */}
        <div style={{ display: "flex", alignItems: "center", gap: "clamp(8px,1vw,14px)", zIndex: 1, minWidth: 0, justifySelf: "start" }}>
          <a
            href="https://www.yogyism.org/" target="_blank" rel="noopener noreferrer"
            style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "clamp(6px,1vw,12px)", cursor: "pointer", minWidth: 0 }}
            onMouseEnter={() => setLogoHovered(true)}
            onMouseLeave={() => setLogoHovered(false)}
          >
             <div style={{ display: "flex", alignItems: "center", gap: "clamp(6px,1vw,12px)" }}>
              {!isMobile && (
              <div style={logoIcon}>
                <YogyismLogoSvg width="100%" height="100%" color="white" style={{ marginTop: "15px" }} />
              </div>
              )}
              {isMobile && (
              <div style={logoIcon}>
                <YogyismLogoSvg width="100%" height="100%" color="white" style={{ marginTop: "10px" }} />
              </div>
              )}
              {!isMobile && (
                 <div style={{ display: "flex", alignItems: "flex-start", gap: 5 }}>
                <div style={logoName}>{ui.header.logo}</div>
                <ExternalLinkIcon size={13} color={logoColor} />
              </div>
              )}
            </div>
          </a>
        </div>

        {/* Center title — grid column 2. A real grid child now (no more
            position:absolute across the full header), so it always centers
            in the space actually left between the logo and the action
            buttons, on both desktop and mobile. */}
        <div style={{
          textAlign: "center", zIndex: 0, minWidth: 0, overflow: "hidden",
          whiteSpace: "nowrap", textOverflow: "ellipsis", pointerEvents: "none",
          fontFamily: FONT_DISPLAY, fontStyle: "normal", fontWeight: 600,
          fontSize: "clamp(16px,1.8vw,26px)", letterSpacing: 0.5, color: accent,
          justifySelf: "center",
        }}>
          {ui.header.title}
        </div>

        {/* Right buttons — grid column 3, pinned to the end */}
        <div style={{ display: "flex", alignItems: "center", gap: "clamp(8px,1vw,14px)", zIndex: 1, justifySelf: "end" }}>
          {!isMobile && (
            <>
              <a
                href="https://ko-fi.com/yogyism"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  ...btnContact,
                  textDecoration: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  lineHeight: 1,
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.opacity = "1";
                  e.currentTarget.style.borderColor = `rgba(${accentRgb},0.75)`;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.opacity = "0.85";
                  e.currentTarget.style.borderColor = `rgba(${accentRgb},0.35)`;
                }}
              >
                <span style={{ color: "#ff5a6e", fontSize: "1.2em", lineHeight: 1 }}>♥</span>
                {ui.header.support}
                <span style={{ color: "#ff5a6e", fontSize: "1.2em", lineHeight: 1 }}>♥</span>
                <ExternalLinkIcon size={11} color="currentColor" />
              </a>
              <button
                style={btnContact}
                onClick={() => setShowContact(true)}
                onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.borderColor = `rgba(${accentRgb},0.75)`; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = "0.85"; e.currentTarget.style.borderColor = `rgba(${accentRgb},0.35)`; }}
              >
                {ui.header.contact}
              </button>

              <LanguageSwitch accentRgb={accentRgb} />

              {user && (
                <button onClick={() => navigate("/session")} style={btnBase}>{ui.header.session}</button>
              )}

              {!user ? (
                <button
                  onClick={handleLoginClick}
                  style={btnContact}
                  onMouseEnter={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.borderColor = `rgba(${accentRgb},0.75)`; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = "0.85"; e.currentTarget.style.borderColor = `rgba(${accentRgb},0.35)`; }}
                >{ui.header.login}</button>
              ) : (
                <button onClick={async () => { await logout(); }} style={btnDanger}>{ui.header.logout}</button>
              )}
            </>
          )}

          {/* Mobile: compact overflow menu keeps the header to a single row so
              the title never has to compete with a row of full-width buttons. */}
          {isMobile && (
            <MobileHeaderMenu
              accent={accent}
              accentRgb={accentRgb}
              user={user}
              onLogin={handleLoginClick}
              onLogout={async () => { await logout(); }}
              onContact={() => setShowContact(true)}
              onSession={() => navigate("/session")}
            />
          )}
        </div>
      </div>

      {/* ✅ Consent modal — shows before LoginModal on first login */}
      {showConsent && (
        <ConsentModal
          onAccept={handleConsentAccept}
          onDecline={handleConsentDecline}
        />
      )}

      <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />

      <ContactModal
        isOpen={showContact}
        onClose={() => setShowContact(false)}
        accent={accent}
        accentRgb={accentRgb}
      />
    </>
  );
};

// ── Mobile overflow menu ─────────────────────────────────────────────────────
// A single "⋯" button that expands into a small dropdown card holding
// Support Us / Contact Us / Session / Login-Logout.
//
// FIX #1 (earlier): dropdown uses `position: fixed`, anchored to the button's
// live on-screen coordinates via getBoundingClientRect(), instead of
// `position: absolute` inside the header — several ancestors (swiper
// wrapper, panels, scroll areas) use `overflow: hidden`, which was clipping
// the old absolutely-positioned dropdown.
//
// FIX #2 (this pass — the actual "menu doesn't open" bug): `position: fixed`
// is normally relative to the viewport, BUT any ancestor with a CSS
// `transform`, `filter`, `perspective`, or `will-change: transform` resets
// that — the fixed element then becomes relative to THAT ancestor instead.
// Swiper wrappers almost always apply `transform: translate3d(...)` to their
// track for the slide animation. So even though the button toggled `open`
// correctly, the dropdown was rendering relative to the (also
// overflow-hidden) swiper container — either off-screen or clipped to
// invisible, while `getBoundingClientRect()` still reported plain viewport
// coordinates. That mismatch is why nothing appeared to happen on click.
//
// The fix is to render the open dropdown through a React Portal straight
// into `document.body`. That completely escapes any transformed or
// overflow-hidden ancestor, so `position: fixed` behaves exactly as
// expected — relative to the real viewport — no matter where in the swiper
// tree this component lives.
const MENU_WIDTH = 190;

function MobileHeaderMenu({
  accent, accentRgb, user, onLogin, onLogout, onContact, onSession,
}: {
  accent: string;
  accentRgb: string;
  user: any;
  onLogin: () => void;
  onLogout: () => void;
  onContact: () => void;
  onSession: () => void;
}) {
  const ui = useUi();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);

  const computeCoords = () => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    // Right-align the menu under the button, clamped so it never runs off
    // either edge of the viewport (matters on very narrow phones).
    let left = rect.right - MENU_WIDTH;
    left = Math.max(8, Math.min(left, window.innerWidth - MENU_WIDTH - 8));
    setCoords({ top: rect.bottom + 8, left });
  };

  const handleToggle = () => {
    if (!open) computeCoords();
    setOpen(v => !v);
  };

  // Keep the menu glued to the button on scroll/resize/orientation change
  useEffect(() => {
    if (!open) return;
    const reposition = () => computeCoords();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  const itemStyle: CSSProperties = {
    display: "flex", alignItems: "center", gap: 8,
    width: "100%", textAlign: "left",
    background: "transparent", border: "none",
    color: "#e8f4fd", fontFamily: mono, fontSize: 12.5,
    letterSpacing: 0.4, padding: "10px 12px", borderRadius: 8,
    cursor: "pointer", textDecoration: "none",
  };

  return (
    <div style={{ position: "relative", zIndex: 5 }}>
      <button
        ref={btnRef}
        aria-label={ui.header.menu}
        aria-expanded={open}
        onClick={handleToggle}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          width: 34, height: 34, borderRadius: 10,
          background: open ? `rgba(${accentRgb},0.16)` : "rgba(255,255,255,0.04)",
          border: `1px solid rgba(${accentRgb},${open ? 0.6 : 0.32})`,
          color: "white", cursor: "pointer", transition: "all 0.15s ease",
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
          <line x1="4" y1="7" x2="20" y2="7" />
          <line x1="4" y1="12" x2="20" y2="12" />
          <line x1="4" y1="17" x2="20" y2="17" />
        </svg>
      </button>

      {open && coords && typeof document !== "undefined" && createPortal(
        <>
          {/* click-away catcher — fixed + full viewport, sits above everything
              except the menu itself, so a tap anywhere else closes the menu
              no matter which scrollable/transformed ancestor the button
              lives inside */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 998 }}
          />
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed", top: coords.top, left: coords.left, zIndex: 999,
              minWidth: MENU_WIDTH, background: "#0b1a2c",
              border: `1px solid rgba(${accentRgb},0.28)`,
              borderRadius: 14, padding: 6,
              boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
              display: "flex", flexDirection: "column", gap: 2,
              animation: "mobileMenuFadeIn 0.16s ease",
            }}
          >
            <style>{`@keyframes mobileMenuFadeIn { from { opacity:0; transform: translateY(-4px);} to { opacity:1; transform: translateY(0);} }`}</style>

            <a
              href="https://paypal.me/MadhanKumarDorairaj" target="_blank" rel="noopener noreferrer"
              style={itemStyle} onClick={() => setOpen(false)}
            >
              <span style={{ color: "#ff5a6e" }}>♥</span> {ui.header.supportMenu}
            </a>

            <button style={itemStyle} onClick={() => { onContact(); setOpen(false); }}>
              💬 {ui.header.contact}
            </button>

            {user && (
              <button style={itemStyle} onClick={() => { onSession(); setOpen(false); }}>
                🧘 {ui.header.session}
              </button>
            )}

            <div style={{ ...itemStyle, cursor: "default", justifyContent: "space-between" }}>
              <span>🌐 {ui.header.language}</span>
              <LanguageSwitch accentRgb={accentRgb} compact />
            </div>

            <div style={{ height: 1, background: `rgba(${accentRgb},0.18)`, margin: "4px 6px" }} />

            {!user ? (
              <button style={{ ...itemStyle, color: accent, fontWeight: 700 }} onClick={() => { onLogin(); setOpen(false); }}>
                → {ui.header.login}
              </button>
            ) : (
              <button style={{ ...itemStyle, color: "#ff9a9a", fontWeight: 700 }} onClick={() => { onLogout(); setOpen(false); }}>
                ⎋ {ui.header.logout}
              </button>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

export default SlideToolbar;